import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { CheckCircle2, Circle, ArrowRight, PlayCircle, PauseCircle, X, FlaskConical } from 'lucide-react';

/**
 * Persistent onboarding banner (spec §5.1 FR-1).
 * Shows on every dashboard page until UnTill + a destination + Go-Live are done.
 * Reads status from /api/onboarding/status; polls once per page mount.
 */
export function OnboardingBanner() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);

  const load = async () => {
    try {
      const r = await api.getOnboardingStatus(token);
      setStatus(r.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { if (token) load(); }, [token, location.pathname]); // eslint-disable-line

  if (!status || status.complete || hidden) return null;
  const s = status.steps;

  const setLive = async (enabled) => {
    setBusy(true);
    try {
      await api.setGoLive(token, enabled);
      toast.success(enabled ? 'Real-time posting enabled' : 'Real-time posting paused');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    } finally { setBusy(false); }
  };

  return (
    <div
      className="rounded-xl border border-blue-500/25 bg-gradient-to-r from-blue-500/[0.08] to-slate-900/40 p-4 mb-4"
      data-testid="onboarding-banner"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="flex items-center gap-3 mb-1">
            <span className="text-sm font-medium text-white">Finish connecting your accounts</span>
            <span className="text-xs text-slate-400">{status.percent}% complete</span>
          </div>
          <div className="w-full h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <div className="h-full bg-blue-500 transition-all" style={{ width: `${status.percent}%` }} />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Step done={s.untill_connected} label="Connect UnTill"
              cta={() => navigate('/untill')} testId="ob-step-untill" />
            <Step done={s.any_destination} label={s.quickbooks_connected ? 'QuickBooks connected' : s.odoo_connected ? 'Odoo connected' : 'Connect QuickBooks or Odoo'}
              cta={() => navigate(s.quickbooks_connected ? '/odoo' : '/quickbooks')} testId="ob-step-dest" />
            <button
              onClick={() => navigate('/dry-run')}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md text-xs bg-slate-800/70 border border-slate-700 text-slate-200 hover:border-blue-500/50 hover:text-white transition-all"
              data-testid="ob-step-dry-run"
            >
              <FlaskConical className="w-3.5 h-3.5"/>
              <span>Preview a dry-run</span>
              <ArrowRight className="w-3 h-3 opacity-60"/>
            </button>
            <Step done={s.go_live_enabled} label="Enable real-time posting"
              cta={s.untill_connected && s.any_destination ? () => setLive(true) : null}
              testId="ob-step-live" busy={busy}/>
          </div>
        </div>
        <button
          onClick={() => setHidden(true)}
          className="text-slate-500 hover:text-white p-1"
          title="Hide until next page"
          data-testid="onboarding-hide"
        >
          <X className="w-4 h-4"/>
        </button>
      </div>
    </div>
  );
}

function Step({ done, label, cta, testId, busy }) {
  return (
    <button
      onClick={cta || undefined}
      disabled={!cta || busy}
      data-testid={testId}
      className={
        'flex items-center gap-2 px-3 py-1.5 rounded-md text-xs transition-all ' +
        (done
          ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 cursor-default'
          : cta
          ? 'bg-slate-800/70 border border-slate-700 text-slate-200 hover:border-blue-500/50 hover:text-white'
          : 'bg-slate-900 border border-slate-800 text-slate-500 cursor-not-allowed')
      }
    >
      {done ? <CheckCircle2 className="w-3.5 h-3.5"/> : <Circle className="w-3.5 h-3.5"/>}
      <span>{label}</span>
      {!done && cta && <ArrowRight className="w-3 h-3 opacity-60"/>}
    </button>
  );
}

/** Compact Go-Live kill switch used on the Dashboard header (FR-8). */
export function GoLiveKillSwitch() {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await api.getOnboardingStatus(token); setStatus(r.data); } catch {}
  };
  useEffect(() => { if (token) load(); }, [token]); // eslint-disable-line

  if (!status) return null;
  const s = status.steps;
  if (!s.untill_connected || !s.any_destination) return null;

  const toggle = async () => {
    setBusy(true);
    try {
      await api.setGoLive(token, !s.go_live_enabled);
      toast.success(!s.go_live_enabled ? 'Real-time posting enabled' : 'Real-time posting paused');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <Button
      onClick={toggle}
      disabled={busy}
      variant="outline"
      className={
        s.go_live_enabled
          ? 'border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10'
          : 'border-amber-500/50 text-amber-300 hover:bg-amber-500/10'
      }
      data-testid="go-live-toggle"
    >
      {s.go_live_enabled
        ? (<><PauseCircle className="w-4 h-4 mr-2"/> Pause syncing</>)
        : (<><PlayCircle className="w-4 h-4 mr-2"/> Enable real-time posting</>)}
    </Button>
  );
}
